import React, { useCallback, useEffect, useState } from "react";
import { business, navLinks, whatsappLink } from "../data/site";
import { ArrowUpRight, WhatsApp } from "./Icons";
import { cx } from "../lib/ui";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  /* Hide on scroll-down, return on scroll-up: the header stays out of the
     way of the imagery but is never more than a flick away. */
  useEffect(() => {
    let last = window.scrollY;
    let frame = 0;

    const measure = () => {
      frame = 0;
      const y = window.scrollY;
      setScrolled(y > 40);
      setHidden(y > 220 && y > last);
      last = y;
    };

    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  /* Spy on the sections so the current one is marked in the header. */
  useEffect(() => {
    const sections = navLinks
      .map((link) => document.getElementById(link.id))
      .filter(Boolean);
    if (!sections.length || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.body.classList.toggle("overflow-hidden", open);
    return () => document.body.classList.remove("overflow-hidden");
  }, [open]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const goTo = useCallback((event, id) => {
    event.preventDefault();
    setOpen(false);
    const target = document.getElementById(id);
    if (!target) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    target.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }, []);

  return (
    <header
      className={cx(
        "fixed inset-x-0 top-0 z-[900] border-b transition-[transform,background-color,border-color,backdrop-filter] duration-[550ms] ease-out",
        /* Solid, not translucent: 60% black over the white sections
           turned the bar into a muddy grey with unreadable links. */
        scrolled
          ? "border-white/15 bg-ink/95 backdrop-blur-xl"
          : "border-transparent",
        hidden && !open ? "-translate-y-[104%]" : "translate-y-0"
      )}
    >
      <div className="flex h-nav items-center justify-between gap-6 px-gutter">
        <a href="#home" onClick={(e) => goTo(e, "home")} className="shrink-0">
          <img
            src="/images/logo-white.png"
            alt={business.name}
            width="200"
            height="51"
            className="h-auto w-[clamp(132px,14vw,178px)]"
          />
        </a>

        <nav className="hidden gap-[clamp(18px,2.6vw,40px)] lg:flex" aria-label="Primary">
          {navLinks.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              onClick={(e) => goTo(e, link.id)}
              className={cx(
                "group relative py-1.5 text-xs font-medium uppercase tracking-[0.16em] transition-colors duration-300",
                active === link.id ? "text-paper" : "text-ash-300 hover:text-paper"
              )}
            >
              {link.label}
              <span
                className={cx(
                  "absolute inset-x-0 bottom-0 h-px bg-paper transition-transform duration-[450ms] ease-out",
                  active === link.id
                    ? "origin-left scale-x-100"
                    : "origin-right scale-x-0 group-hover:origin-left group-hover:scale-x-100"
                )}
              />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3.5">
          <a
            className="hidden items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.16em] transition-colors duration-[350ms] ease-out hover:border-paper hover:bg-paper hover:text-ink lg:inline-flex"
            href={whatsappLink}
            target="_blank"
            rel="noreferrer noopener"
          >
            <WhatsApp width="16" height="16" />
            <span>WhatsApp</span>
            <ArrowUpRight width="14" height="14" />
          </a>

          <button
            type="button"
            aria-expanded={open}
            aria-controls="nav-panel"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
            className="group -mr-2 grid h-11 w-11 content-center justify-items-end gap-1.5 lg:hidden"
          >
            <i
              className={cx(
                "block h-px w-[26px] bg-paper transition-transform duration-[450ms] ease-smooth",
                open && "translate-y-[3.5px] rotate-45"
              )}
            />
            <i
              className={cx(
                "block h-px bg-paper transition-[transform,width] duration-[450ms] ease-smooth",
                open ? "w-[26px] -translate-y-[3.5px] -rotate-45" : "w-[18px] group-hover:w-[26px]"
              )}
            />
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      <div
        id="nav-panel"
        hidden={!open}
        className="max-h-[calc(100dvh-theme(spacing.nav))] overflow-y-auto border-b border-white/15 bg-ink px-gutter pb-9 pt-2 lg:hidden"
      >
        <ul className="border-t border-white/15">
          {navLinks.map((link, i) => (
            <li
              key={link.id}
              className="animate-fade-up border-b border-white/15 opacity-0"
              style={{ animationDelay: `${i * 60 + 60}ms` }}
            >
              <a
                href={`#${link.id}`}
                onClick={(e) => goTo(e, link.id)}
                className="flex items-baseline gap-4 py-4 font-display text-[clamp(30px,8vw,46px)] leading-none transition-[padding,color] duration-[400ms] ease-out hover:pl-3 hover:text-ash-200"
              >
                <em className="font-sans text-[11px] not-italic tracking-[0.22em] text-ash-400">
                  {String(i + 1).padStart(2, "0")}
                </em>
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="mt-7 flex flex-wrap gap-x-7 gap-y-2.5 text-xs uppercase tracking-[0.14em] text-ash-300">
          <a href={`tel:${business.phoneDial}`} className="hover:text-paper">
            {business.phoneDisplay}
          </a>
          <a
            href={business.instagram}
            target="_blank"
            rel="noreferrer noopener"
            className="hover:text-paper"
          >
            {business.instagramHandle}
          </a>
        </div>
      </div>
    </header>
  );
}
